/**
 * Vorlage „Waschen“.
 *
 * Ein Wasserhahn öffnet sich, ein Strahl fällt auf Salat, Gemüse oder Reis
 * im Sieb. Das Sieb wird dabei leicht geschwenkt.
 */
import { ease, random, segment, wave } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { blobPoints, path, roundedRectPath, smooth } from '../paths.ts';
import { circle, compact, ellipse, group, line, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { sizzle } from '../parts/effects.ts';
import { colander, stream } from '../parts/kitchen.ts';
import { counter, doubleArrow, GROUND_Y } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Sieb auf der Arbeitsfläche. */
const SIEVE = { cx: 120, width: 122, height: 40 } as const;
const RIM_Y = GROUND_Y - SIEVE.height;
/** Auslauf des Wasserhahns. */
const SPOUT = { x: 112, y: 34 } as const;

/** Zeitplan: Hahn auf, Wasser läuft, Hahn zu. */
const TIME = { open: [0.04, 0.12], close: [0.8, 0.88] } as const;

/** Was gewaschen wird. */
const ITEMS = { salad: 'Salat', vegetables: 'Gemüse', rice: 'Reis' } as const;
type Item = keyof typeof ITEMS;

/** Wasserhahn: Säule von oben, gebogener Auslauf, Hebel oben. */
function tap(lever: number): SceneNode[] {
  const pipe = path()
    .move(SPOUT.x - 46, 0)
    .line(SPOUT.x - 46, 12)
    .quad(SPOUT.x - 46, 24, SPOUT.x - 30, 24)
    .line(SPOUT.x - 6, 24)
    .quad(SPOUT.x + 6, 24, SPOUT.x + 6, 32)
    .line(SPOUT.x + 6, SPOUT.y)
    .line(SPOUT.x - 6, SPOUT.y)
    .line(SPOUT.x - 6, 34)
    .quad(SPOUT.x - 6, 32, SPOUT.x - 10, 32)
    .line(SPOUT.x - 30, 32)
    .quad(SPOUT.x - 54, 32, SPOUT.x - 54, 12)
    .line(SPOUT.x - 54, 0)
    .close()
    .toString();
  return [
    group([shape(roundedRectPath(SPOUT.x - 52, 2, 26, 6, 3), styles.solid('ink'))], {
      transform: { rotate: lever * 20, ox: SPOUT.x - 50, oy: 5 },
    }),
    shape(pipe, styles.solid('paper')),
  ];
}

/** Inhalt des Siebs. */
function load(item: Item, shake: number): SceneNode[] {
  const cx = SIEVE.cx + shake;
  if (item === 'salad') {
    const rng = random(14);
    return [-30, -10, 10, 30, -18, 20].map((dx, index) => {
      const y = RIM_Y - 2 + (index > 3 ? 6 : 0);
      const outline = smooth(
        blobPoints(cx + dx, y, 16, 10, 12, () => 0.82 + rng() * 0.3),
        true,
        0.9,
      );
      return group([
        shape(outline, styles.solid(index % 2 === 0 ? 'shade' : 'tint')),
        line(cx + dx - 9, y + 2, cx + dx + 8, y - 2, styles.fine('deep')),
      ]);
    });
  }
  if (item === 'vegetables') {
    const spots: readonly Point[] = [
      { x: -32, y: 0 },
      { x: -12, y: -4 },
      { x: 10, y: -2 },
      { x: 30, y: 1 },
      { x: -22, y: 6 },
      { x: 1, y: 7 },
      { x: 22, y: 7 },
    ];
    return spots.map((spot) =>
      group([
        circle(cx + spot.x, RIM_Y + spot.y - 2, 9, styles.solid('tint')),
        ellipse(cx + spot.x - 3, RIM_Y + spot.y - 6, 2.5, 1.5, styles.flat('paper')),
        line(cx + spot.x, RIM_Y + spot.y - 11, cx + spot.x + 2, RIM_Y + spot.y - 14, styles.detail('deep')),
      ]),
    );
  }
  // Reis: flacher Haufen mit Körnern.
  const heap = path()
    .move(cx - 46, RIM_Y + 8)
    .quad(cx, RIM_Y - 14, cx + 46, RIM_Y + 8)
    .close()
    .toString();
  const rng = random(5);
  const grains: SceneNode[] = Array.from({ length: 22 }, () => {
    const x = cx - 34 + rng() * 68;
    const y = RIM_Y - 2 + rng() * 9;
    return ellipse(x, y, 2, 1, styles.flat('shade'));
  });
  return [shape(heap, styles.snow()), group(grains, { clip: heap })];
}

/** Vorlage „Waschen“. */
export const prepWashTemplate = defineTemplate({
  id: 'prep.wash',
  group: 'prep',
  title: 'Waschen',
  description:
    'Salat, Gemüse oder Reis werden im Sieb unter fließendem kaltem Wasser gewaschen und dabei leicht geschwenkt. Danach gut abtropfen lassen.',
  durationSeconds: 5,
  keywords: [
    'waschen',
    'gewaschen',
    'abwaschen',
    'abspülen',
    'abbrausen',
    'unter fließendem wasser',
    'kalt abspülen',
    'säubern',
    'trocken schleudern',
  ],
  stillTime: 0.45,
  parameters: [choice('item', 'Zutat', ITEMS, 'salad')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/salat|kräuter|spinat|rucola/.test(lower)) return { item: 'salad' };
    if (/reis|linsen|quinoa|hirse/.test(lower)) return { item: 'rice' };
    if (/gemüse|tomate|kartoffel|beeren|obst|äpfel|pilze/.test(lower)) return { item: 'vegetables' };
    return {};
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'salad') as Item;
    const lever = ease.inOutCubic(segment(t, ...TIME.open)) - ease.inOutCubic(segment(t, ...TIME.close));
    const flow = lever;
    const shake = wave(t, 4) * 4 * lever;
    const sieve = colander(SIEVE.cx + shake, RIM_Y, SIEVE.width, SIEVE.height);
    const landing = { x: SPOUT.x + shake * 0.5, y: RIM_Y - 6 };

    return compact([
      ...counter(),
      ...sieve.back,
      group(load(item, shake), { clip: `${sieve.opening}M0 0H240V${RIM_Y}H0Z` }),
      ...sieve.front,
      flow > 0.02
        ? stream(
            [{ x: SPOUT.x, y: SPOUT.y }, { x: SPOUT.x, y: (SPOUT.y + landing.y) / 2 }, landing],
            8 * flow,
            t,
            'tint',
          )
        : null,
      ...(flow > 0.02 ? sizzle(landing.x, landing.y, 22, flow, t, 4) : []),
      ...tap(lever),
      line(SPOUT.x - 6, SPOUT.y, SPOUT.x + 6, SPOUT.y, { stroke: 'ink', strokeWidth: STROKE.detail }),
    ]);
  },
  hints() {
    return [doubleArrow({ x: SIEVE.cx - 40, y: GROUND_Y + 12 }, { x: SIEVE.cx + 40, y: GROUND_Y + 12 })];
  },
});
